<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="groupCustomerAccountCreationSuccess.length || groupCustomerAccountCreationFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="groupCustomerAccountCreationSuccess.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="success">
              {{ $t('SUCCESS LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="groupCustomerAccountCreationSuccessReport"
            :items="groupCustomerAccountCreationSuccess"
            responsive
            :fields="successHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
        <b-col
          v-if="groupCustomerAccountCreationFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="groupCustomerAccountCreationFailedReport"
            :items="groupCustomerAccountCreationFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="6">
          <validation-observer ref="individualCustomerAccountRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col md="5">
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && parseExcelFile.length"
                :label="$t('Next >')"
                size="md"
                :hidden="isNextValidateHidden"
                :loading="isLoading"
                @click="handleNextValidate"
              />
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && !errorMessages.length"
                :label="$t('Create Group Customer Accounts')"
                size="md"
                :hidden="isCreateBatchHidden"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleCreate"
              />
            </b-col>
            <b-col md="7">
              <Button
                class="ml-lg-2"
                align="end"
                :label="$t('Download Group Customer Account Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="groupCustomerAccountCreationSuccessReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <b-table
      v-if="!errorMessages.length"
      ref="groupCustomerAccountCreation"
      :items="parseExcelFile"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
    </b-table>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
// import Papa from 'papaparse'
import XLSX from 'xlsx'
import Button from '../../../components/button/Button.vue'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../../helpers/data'
import { toYearWithDate } from '../../../../helpers/formatDate'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'oldGroupCustomerID', sortable: true, label: 'Old Group Customer ID' },
        { key: 'customerType', sortable: true, label: 'Customer Type' },
        { key: 'groupName', sortable: true, label: 'Group\'s Name' },
        { key: 'description', sortable: true, label: 'Description' },
        { key: 'businessPhoneNumber', sortable: true, label: 'Group\'s Phone Number' },
        { key: 'email', sortable: true, label: 'Group\'s Email' },
        { key: 'address', sortable: true, label: 'Group\'s Address' },
        { key: 'countryOfOperation', sortable: true, label: 'Country of Operation' },
        { key: 'stateOfOperation', sortable: true, label: 'State of Operation' },
        { key: 'localGovernmentOfOperation', sortable: true, label: 'Local Govt of Operation' },
        { key: 'contactPersonName', sortable: true, label: 'Group Contact Person\'s Name' },
        { key: 'contactPersonPhoneNumber', sortable: true, label: 'Group Contact Person\'s Phone No' },
        { key: 'natureOfBusiness', sortable: true, label: 'Nature of Business' },
        { key: 'industrialSector', sortable: true, label: 'Group\'s Industrial Sector' },
        { key: 'registrationDate', sortable: true, label: 'Group\'s Incorporation Date' },
        { key: 'registrationNumber', sortable: true, label: 'Group\'s Registration Number' },
        { key: 'tin', sortable: true, label: 'Tax Identification Number' },
        { key: 'signatory', label: 'Signatory' },
        { key: 'mandateRule', label: 'Mandate Rule' },
        { key: 'referenceAccountNumber', sortable: true, label: 'Reference Account Number' },
        { key: 'referenceNuban', sortable: true, label: 'Reference NUBAN' },
        { key: 'bvn', sortable: true, label: 'Bank Verification Number' },
        { key: 'accountName', sortable: true, label: 'Group Account' },
        { key: 'accountType', sortable: true, label: 'Account Type' },
        { key: 'accountStatus', sortable: true, label: 'Account Status' },
        { key: 'branchCode', sortable: true, label: 'Branch Code' },
        { key: 'productCode', sortable: true, label: 'Product Code' },
        { key: 'accountOfficerStaffID', sortable: true, label: 'Account Officer Staff ID' },
        { key: 'accountTierLevel', sortable: true, label: 'Account Tier Level' },
        { key: 'accessLevel', sortable: true, label: 'Access Level' },
        { key: 'enableEmailNotification', sortable: true, label: 'Email Notification' },
        { key: 'enableSMSNotification', sortable: true, label: 'SMS Notification' },
        { key: 'statementDeliveryMode', sortable: true, label: 'Statement Delivery Mode' },
        { key: 'statementDeliveryFrequency', sortable: true, label: 'Statement Delivery Frequency' },
        { key: 'minimumBalanceRequired', sortable: true, label: 'Minimum Balance Required' },
        { key: 'referee1CustomerID', sortable: true, label: 'Referee 1 Customer ID' },
        { key: 'referee2CustomerID', sortable: true, label: 'Referee 2 Customer ID' },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      isNextValidateHidden: false,
      groupCustomerAccountCreationSuccess: [],
      groupCustomerAccountCreationFailed: [],
      sortDesc: false,
      sortBy: 'groupName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      groupCustomerAccountCreation: 'batch/groupCustomerAccountCreation',
      downloadAccountFormat: 'batch/downloadAccountFormat',
      validateGroupCustomerAccountCreation: 'batch/validateGroupCustomerAccountCreation',
    }),
    async handleCreate() {
      this.$refs.individualCustomerAccountRules.validate().then(success => {
        if (success) {
          this.confirm(this.groupCustomerAccountCreationAction, { text: 'Are you sure you want to Create Group Customer Accounts?' })
        }
      })
    },
    async groupCustomerAccountCreationAction() {
      try {
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          customerType: item.customerType ? Number(item.customerType) : '',
          email: item.email ? item.email : null,
          businessPhoneNumber: item.businessPhoneNumber ? item.businessPhoneNumber : null,
          registrationNumber: item.registrationNumber ? item.registrationNumber : null,
          registrationDate: item.registrationDate ? toYearWithDate(item.registrationDate) : null,
          accountType: item.accountType ? Number(item.accountType) : '',
          industrialSector: item.industrialSector ? Number(item.industrialSector) : null,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : '',
          accountTierLevel: item.accountTierLevel ? Number(item.accountTierLevel) : 1,
          accessLevel: item.accessLevel ? Number(item.accessLevel) : 1,
          enableEmailNotification: item.enableEmailNotification ? Boolean(item.enableEmailNotification) : false,
          enableSMSNotification: item.enableSMSNotification ? Boolean(item.enableSMSNotification) : false,
          statementDeliveryMode: item.statementDeliveryMode ? Number(item.statementDeliveryMode) : null,
          statementDeliveryFrequency: item.statementDeliveryFrequency ? Number(item.statementDeliveryFrequency) : null,
          minimumBalanceRequired: item.minimumBalanceRequired ? Number(item.minimumBalanceRequired) : 0,
        }))
        const response = await this.groupCustomerAccountCreation({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          this.groupCustomerAccountCreationSuccess = data.success
          this.groupCustomerAccountCreationFailed = data.failed
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$('Your request to create Group Customer Accounts failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            oldGroupCustomerID: items['Old Group Customer ID'],
            customerType: items['Customer Type'],
            groupName: items['Group Name'],
            description: items.Description,
            businessPhoneNumber: items['Group\'s Phone Number'],
            email: items['Group\'s Email'],
            address: items['Group\'s Address'],
            countryOfOperation: items['Country of Operation'],
            stateOfOperation: items['State of Operation'],
            localGovernmentOfOperation: items['Local Govt of Operation'],
            contactPersonName: items['Group Contact Person\'s Name'],
            contactPersonPhoneNumber: items['Group\'s Contact Person Phone No'],
            natureOfBusiness: items['Nature of Business'],
            industrialSector: items['Group\'s Industrial Sector'],
            registrationDate: items['Group\'s Incorporation Date'],
            registrationNumber: items['Group\'s Registration Number'],
            tin: items['Tax Identification Number'],
            signatory: items['Signatory '],
            mandateRule: items['Mandate Rule'],
            referenceAccountNumber: items['Reference Account Number'],
            referenceNuban: items['Reference NUBAN'],
            bvn: items['Bank Verification Number'],
            accountName: items['Group Account Name'],
            accountType: items['Account Type'],
            accountStatus: items['Account Status'],
            branchCode: items['Branch Code'],
            productCode: items['Product Code'],
            accountOfficerStaffID: items['Account Officer Staff ID'],
            accountTierLevel: items['Account Tier Level'],
            accessLevel: items['Access Level'],
            enableEmailNotification: items['Enable Email Notification'],
            enableSMSNotification: items['Enable SMS Notification'],
            statementDeliveryMode: items['Statement Delivery Mode'],
            statementDeliveryFrequency: items['Statement Delivery Frequency'],
            minimumBalanceRequired: items['Minimum Balance Required'],
            referee1CustomerID: items['Referee1 Customer ID'],
            referee2CustomerID: items['Referee2 Customer ID'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleNextValidate() {
      try {
        this.hasFileData = []
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          customerType: item.customerType ? Number(item.customerType) : '',
          email: item.email ? item.email : null,
          businessPhoneNumber: item.businessPhoneNumber ? item.businessPhoneNumber : null,
          registrationNumber: item.registrationNumber ? item.registrationNumber : null,
          registrationDate: item.registrationDate ? toYearWithDate(item.registrationDate) : null,
          accountType: item.accountType ? Number(item.accountType) : '',
          industrialSector: item.industrialSector ? Number(item.industrialSector) : null,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : '',
          accountTierLevel: item.accountTierLevel ? Number(item.accountTierLevel) : 1,
          accessLevel: item.accessLevel ? Number(item.accessLevel) : 1,
          enableEmailNotification: item.enableEmailNotification ? Boolean(item.enableEmailNotification) : false,
          enableSMSNotification: item.enableSMSNotification ? Boolean(item.enableSMSNotification) : false,
          statementDeliveryMode: item.statementDeliveryMode ? Number(item.statementDeliveryMode) : null,
          statementDeliveryFrequency: item.statementDeliveryFrequency ? Number(item.statementDeliveryFrequency) : null,
          minimumBalanceRequired: item.minimumBalanceRequired ? Number(item.minimumBalanceRequired) : 0,
        }))
        const response = await this.validateGroupCustomerAccountCreation({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.errorMessages = data.failed
          this.isCreateBatchHidden = false
          if (this.errorMessages.length) {
            this.isNextValidateHidden = false
          } else {
            this.isNextValidateHidden = true
            this.alertSuccess({ text: this.$t('Everything seems good! Continue with Group Customer Account Creation') })
          }
          this.isDisabled = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const text = this.$t('Your request to perform Batch Group Customer Accounts validation failed. Reason')
          this.alertError({
            text: `${text}: ${responseError.errorMessage}`,
          })
        })
        this.isCreateBatchHidden = true
        this.isDisabled = true
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Group Customer Account Creation Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const GROUP_CUSTOMER_ACCOUNT_CREATION_TEMPLATE = 14
        const response = await this.downloadAccountFormat(GROUP_CUSTOMER_ACCOUNT_CREATION_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Group-Customer-Account-Creation-Data-Migration.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Group Customer Account Creation Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download Group Customer Account Creation Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
